import { TypeInitProps, TypeRequest } from '../../types';
import { encryptParams } from '../../utils/index';
import { WEB_URL } from '../../utils/urls';
export default class ForgetPwdAPI {
  private request: any;
  constructor(props: TypeInitProps & { request: TypeRequest }) {
    const { request } = props;
    this.request = request;
  }

  /**
   * @param {string} mobile - the mobile used to verify user's identity
   * @return {Promise} send result
   */
  sendCode = (params: { mobile: string; extra_params?: object }) => {
    const { mobile, extra_params = {} } = params;
    const data = encryptParams({ mobile, type: 6, ...extra_params }, [
      'mobile',
      'type',
    ]);
    return new Promise((resolve, reject) => {
      this.request({
        url: WEB_URL.SEND_MOBILE_CODE,
        method: 'POST',
        data,
      })
        .then((data: any) => {
          resolve(data);
        })
        .catch((e: any) => {
          reject(e);
        });
    });
  };

  /**
   * @param {string} mobile - mobile to be verified
   * @param {string} code - the captcha
   * @return {Promise} - verify result
   */
  validateCode = (params: {
    mobile: string;
    code: string;
    extra_params?: object;
  }) => {
    const { mobile, code, extra_params = {} } = params;
    const data = encryptParams({ mobile, code, type: 6, ...extra_params }, [
      'mobile',
      'code',
      'type',
    ]);
    return new Promise((resolve, reject) => {
      this.request({
        url: WEB_URL.CHECK_MOBILE_CODE,
        method: 'POST',
        data,
      })
        .then((data: any) => {
          resolve(data);
        })
        .catch((e: any) => {
          reject(e);
        });
    });
  };

  /**
   * @param {string} password - the new password
   * @param {string} mobile - the mobile used to verify user's identity
   * @param {string} code - the captcha
   * @return {Promise} reset result
   */
  resetPwdByMobile = (params: {
    password: string;
    mobile: string;
    code: string;
    extra_params?: object;
  }) => {
    const { password, mobile, code, extra_params = {} } = params;
    const data = encryptParams({ password, mobile, code, ...extra_params }, [
      'password',
      'mobile',
      'code',
    ]);
    return new Promise((resolve, reject) => {
      this.request({
        url: WEB_URL.RESET_PWD_BY_MOBILE,
        method: 'POST',
        data,
      })
        .then((data: any) => {
          resolve(data);
        })
        .catch((e: any) => {
          reject(e);
        });
    });
  };

  /**
   * @param {string} email - the email used to verify user's identity
   * @return {Promise} send result
   */
  sendEmailCode = (params: { email: string; extra_params?: object }) => {
    const { email, extra_params = {} } = params;
    const data = encryptParams({ email, type: 4, ...extra_params }, [
      'email',
      'type',
    ]);
    return new Promise((resolve, reject) => {
      this.request({
        url: WEB_URL.SEND_EMAIL_CODE,
        method: 'POST',
        data,
      })
        .then((data: any) => {
          resolve(data);
        })
        .catch((e: any) => {
          reject(e);
        });
    });
  };

  /**
   * @param {string} email - email to be verified
   * @param {string} code - the captcha
   * @return {Promise} - verify result
   */
  validateEmail = (params: {
    email: string;
    code: string;
    extra_params?: object;
  }) => {
    const { email, code, extra_params = {} } = params;
    const data = encryptParams({ email, code, type: 4, ...extra_params }, [
      'email',
      'code',
      'type',
    ]);
    return this.request({
      url: WEB_URL.CHECK_EMAIL_CODE,
      method: 'POST',
      data,
    });
  };

  /**
   * @param {string} password - the new password
   * @param {string} email - the email used to verify user's identity
   * @param {string} code - the captcha
   * @return {Promise} reset result
   */
  resetPwdByEmail = (params: {
    password: string;
    email: string;
    code: string;
    extra_params?: object;
  }) => {
    const { password, email, code, extra_params = {} } = params;
    const data = encryptParams({ password, email, code, ...extra_params }, [
      'password',
      'email',
      'code',
    ]);
    return new Promise((resolve, reject) => {
      this.request({
        url: WEB_URL.RESET_PWD_BY_EMAIL,
        method: 'POST',
        data,
      })
        .then((data: any) => {
          resolve(data);
        })
        .catch((e: any) => {
          reject(e);
        });
    });
  };

  /**
   * @param {string} password - the new password
   * @param {string} ticket - the ticket returned after mobile verification
   * @return {Promise} reset result
   */
  resetPwdByMobileTicket = (params: {
    password: string;
    ticket: string;
    extra_params?: object;
  }) => {
    const { password, ticket, extra_params = {} } = params;
    const data = encryptParams({ password, ticket, ...extra_params }, [
      'password',
    ]);
    return new Promise((resolve, reject) => {
      this.request({
        url: WEB_URL.RESET_PWD_BY_MOBILE_TICKET,
        method: 'POST',
        data,
      })
        .then((data: any) => {
          resolve(data);
        })
        .catch((e: any) => {
          reject(e);
        });
    });
  };

  /**
   * @param {string} password - the new password
   * @param {string} ticket - the ticket returned after email verification
   * @return {Promise} reset result
   */
  resetPwdByEmailTicket = (params: {
    password: string;
    ticket: string;
    extra_params?: object;
  }) => {
    const { password, ticket, extra_params = {} } = params;
    const data = encryptParams({ password, ticket, ...extra_params }, [
      'password',
    ]);
    return new Promise((resolve, reject) => {
      this.request({
        url: WEB_URL.RESET_PWD_BY_EMAIL_TICKET,
        method: 'POST',
        data,
      })
        .then((data: any) => {
          resolve(data);
        })
        .catch((e: any) => {
          reject(e);
        });
    });
  };

  /**
   * @param {string} password - the new password
   * @param {string} ticket - the ticket returned after mobile verification
   * @param {string} conditional_login_ticket - the ticket by login error
   * @return {Promise} reset result
   */
  forceResetPwdByMobileTicket = (params: {
    password: string;
    ticket: string;
    code: string;
    conditional_login_ticket: string;
    extra_params?: object;
  }) => {
    const {
      password,
      ticket,
      conditional_login_ticket,
      code,
      extra_params = {},
    } = params;
    const data = encryptParams(
      { password, ticket, conditional_login_ticket, code, ...extra_params },
      ['password']
    );
    return new Promise((resolve, reject) => {
      this.request({
        url: WEB_URL.FORCE_RESET_PWD_BY_MOBILE_TICKET,
        method: 'POST',
        data,
      })
        .then((data: any) => {
          resolve(data);
        })
        .catch((e: any) => {
          reject(e);
        });
    });
  };

  /**
   * @param {string} password - the new password
   * @param {string} ticket - the ticket returned after email verification
   * @param {string} conditional_login_ticket - the ticket by login error
   * @return {Promise} reset result
   */
  forceResetPwdByEmailTicket = (params: {
    password: string;
    ticket: string;
    code: string;
    conditional_login_ticket: string;
    extra_params?: object;
  }) => {
    const {
      password,
      ticket,
      conditional_login_ticket,
      code,
      extra_params = {},
    } = params;
    const data = encryptParams(
      { password, ticket, conditional_login_ticket, code, ...extra_params },
      ['password']
    );
    return new Promise((resolve, reject) => {
      this.request({
        url: WEB_URL.FORCE_RESET_PWD_BY_EMAIL_TICKET,
        method: 'POST',
        data,
      })
        .then((data: any) => {
          resolve(data);
        })
        .catch((e: any) => {
          reject(e);
        });
    });
  };
}
